import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from "react-router-dom";
import Layout from '../../shared/components/Layout';
import AlertMessages from '@/components/AlertMessages';
import InfoDisplay from '@/components/InfoDisplay';
import { InputFieldBox } from '@/components/InputField';
import { DateFieldBox } from '@/components/DateField';
import { SelectInputBox } from '@/components/SelectInput';
import { LensDataForm } from '../components/LensDataForm';
import SidebarNavigation from '@/components/SidebarNavigation';
import type { FacilityLensOrderParams, OrderClItem, LensProduct } from '../types';
import type Facility from '../../shared/types/Facility';
import confirmFacilityLens from '../api/confirmFacilityLens';
import checkExistCustomerOrder from '../api/checkExistCustomerOrder';
import { toUnderscoreCase, formatMonth } from '@/utils/format';
import getFacilityLens from '../api/getFacilityLens';
import { useOrderContext } from '@/providers/OrderProvider';
import useSelectedProduct from '../hooks/useSelectedProduct';
import useLensOrderInfo from '../hooks/useLensOrderInfo';
import clearLocalStorage from '@/utils/clearLocalStorage';
import findStartMonth from '../api/findStartMonth';

const FacilityLens: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { arrivalTimeZoneLabels, orderClassificationLabels, getProductOptions, handleProductIdChange } = useOrderContext();

  const [facility, setFacility] = useState<Facility>();
  const [products, setProducts] = useState<LensProduct[]>([]);

  const [custNameKana, setCustNameKana] = useState<string>('');
  const [custCodeSuffix, setCustCodeSuffix] = useState<string>('');
  const [orderClassification, setOrderClassification] = useState<string>('brand_new');
  const [note, setNote] = useState<string>('');
  const [arrivalDate, setArrivalDate] = useState<string>('');
  const [arrivalTimeZone, setArrivalTimeZone] = useState<string>('no_preference');
  const [expectedShipDate, setExpectedShipDate] = useState<string>('');
  const [shortestArrivalDate, setShortestArrivalDate] = useState<string>('');
  const [startMonth, setStartMonth] = useState<string>();

  const [rightLens, setRightLens] = useState<OrderClItem>({
    fc: undefined,
    tp: undefined,
    cyl: undefined,
    dia: undefined,
    qty: 1,
    eyeType: "right",
    productId: undefined,
  });
  const [leftLens, setLeftLens] = useState<OrderClItem>({
    fc: undefined,
    tp: undefined,
    cyl: undefined,
    dia: undefined,
    qty: 1,
    eyeType: "left",
    productId: undefined,
  });

  const [isRightLensOrdered, setIsRightLensOrdered] = useState<boolean>(true)
  const [isLeftLensOrdered, setIsLeftLensOrdered] = useState<boolean>(true)

  const [productName, setProductName] = useState<string>()
  const [isAstigma, setIsAstigma] = useState<boolean>(false);
  const [rightStockOut, setRightStockOut] = useState<string>();
  const [leftStockOut, setLeftStockOut] = useState<string>();
  const [productId, setProductId] = useState<number>();
  const [restrict, setRestrict] = useState<string>();
  const [qtyMax, setQtyMax] = useState<number>(0);

  const [alertMessageOpen, setAlertMessageOpen] = useState<boolean>(false);
  const [alertMessages, setAlertMessages] = useState<string[]>([]);
  const [arrivalDates, setArrivalDates] = useState<string[]>([]);

  const [initialExpectedShipDate, setInitialExpectedShipDate] = useState<string>('');
  const [initialShortestArrivalDate, setInitialShortestArrivalDate] = useState<string>('');
  const [initialArrivalDates, setInitialArrivalDates] = useState<string[]>([]);

  const [customerNewOrder, setCustomerNewOrder] = useState<boolean>(false);
  const [applicantNameOpen, setApplicantNameOpen] = useState<boolean>(true);
  const [applicant, setApplicant] = useState({
    nameKana: "",
    birthday: "",
  })

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getFacilityLens();

        setFacility(response.data.facility);
        setProducts(response.data.products);

        setExpectedShipDate(response.data.expectedShipDate);
        setInitialExpectedShipDate(response.data.expectedShipDate);

        setShortestArrivalDate(response.data.shortestArrivalDate);
        setInitialShortestArrivalDate(response.data.shortestArrivalDate);

        setArrivalDates(response.data.arrivalDates);
        setInitialArrivalDates(response.data.arrivalDates);
      } catch (error) {
        console.error("Error fetching facility lens data:", error);
      }
    };

    fetchData();

    const fromConfirmPage = location.state?.fromConfirmPage;
    if(fromConfirmPage) {
      const storedOrderParams = localStorage.getItem('orderParams');
      if (storedOrderParams) {
        const orderParams = JSON.parse(storedOrderParams);

        const {
          custNameKana,
          orderClassification,
          note,
          expectedShipDate,
          shortestArrivalDate,
          arrivalDate,
          arrivalTimeZone,
          orderClItemsAttributes,
          custCodeSuffix,
        } = orderParams.order;

        const rightLens = orderClItemsAttributes.find((lens: OrderClItem) => lens.eyeType === 'right');
        const leftLens = orderClItemsAttributes.find((lens: OrderClItem) => lens.eyeType === 'left');

        setCustNameKana(custNameKana);
        setOrderClassification(orderClassification);
        setNote(note);
        setExpectedShipDate(expectedShipDate);
        setShortestArrivalDate(shortestArrivalDate);
        setArrivalDate(arrivalDate);
        setArrivalTimeZone(arrivalTimeZone);
        setCustCodeSuffix(custCodeSuffix);
        setRightLens(rightLens);
        setLeftLens(leftLens);

        if(rightLens) {
          setProductId(rightLens.productId);
        } else if (leftLens) {
          setProductId(leftLens.productId);
        }
      }

      const storedFacilityName = localStorage.getItem("facilityName");
      if(storedFacilityName) {
        setFacility((prevFacility: Facility | undefined) => {
          if (prevFacility) {
            return { ...prevFacility, name: JSON.parse(storedFacilityName) };
          }
          return prevFacility;
        });
      }

      const storedFacilityCode = localStorage.getItem("facilityCode");
      if(storedFacilityCode) {
        setFacility((prevFacility: Facility | undefined) => {
          if (prevFacility) {
            return { ...prevFacility, facilityCode: JSON.parse(storedFacilityCode) };
          }
          return prevFacility;
        });
      }

      const storedFacilityFlatRateCode = localStorage.getItem("facilityFlatRateCode");
      if(storedFacilityFlatRateCode) {
        setFacility((prevFacility: Facility | undefined) => {
          if (prevFacility) {
            return { ...prevFacility, facilityFlatRateCode: JSON.parse(storedFacilityFlatRateCode) };
          }
          return prevFacility;
        });
      }

      const storedFacilityId = localStorage.getItem("facilityId");
      if(storedFacilityId) {
        setFacility((prevFacility: Facility | undefined) => {
          if (prevFacility) {
            return { ...prevFacility, id: JSON.parse(storedFacilityId) };
          }
          return prevFacility;
        });
      }

      const storedIsRightLensOrdered = localStorage.getItem("isRightLensOrdered");
      if(storedIsRightLensOrdered) {
        setIsRightLensOrdered(JSON.parse(storedIsRightLensOrdered));
      }

      const storedIsLeftLensOrdered = localStorage.getItem("isLeftLensOrdered");
      if(storedIsLeftLensOrdered) {
        setIsLeftLensOrdered(JSON.parse(storedIsLeftLensOrdered));
      }

      const storedProductName = localStorage.getItem("productName");
      if(storedProductName) {
        setProductName(JSON.parse(storedProductName));
      }

      const storedStartMonth = localStorage.getItem("startMonth");
      if(storedStartMonth) {
        setStartMonth(JSON.parse(storedStartMonth));
      }

      const storedApplicant = localStorage.getItem("applicant");
      if(storedApplicant) {
        setApplicant(JSON.parse(storedApplicant));
      }

      const storedCustomerNewOrder = localStorage.getItem("customerNewOrder");
      if(storedCustomerNewOrder) {
        setCustomerNewOrder(JSON.parse(storedCustomerNewOrder));
      }

      const storedapplicantNameOpen = localStorage.getItem("applicantNameOpen");
      if(storedapplicantNameOpen) {
        setApplicantNameOpen(JSON.parse(storedapplicantNameOpen));
      }
    } else {
      clearLocalStorage();
    }
  }, [location.state?.fromConfirmPage]);

  useSelectedProduct({
    products,
    productId,
    rightLens,
    leftLens,
    setQtyMax,
    setIsAstigma,
    setRestrict,
    setRightLens,
    setLeftLens,
    setProductName,
    setIsRightLensOrdered,
    setIsLeftLensOrdered,
  });

  useLensOrderInfo({
    facilityId: facility?.id,
    products,
    productId,
    rightLens,
    leftLens,
    isRightLensOrdered,
    isLeftLensOrdered,
    setExpectedShipDate,
    setShortestArrivalDate,
    setArrivalDates,
    setRightStockOut,
    setLeftStockOut,
    initialExpectedShipDate,
    initialShortestArrivalDate,
    initialArrivalDates
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    let orderClItemsAttributes = [];
    if (isRightLensOrdered) {
      orderClItemsAttributes.push(rightLens);
    }
    if (isLeftLensOrdered) {
      orderClItemsAttributes.push(leftLens);
    }

    const params: FacilityLensOrderParams = {
      order: {
        order_type: 'facility',
        facilityId: facility?.id,
        custNameKana: custNameKana,
        orderClassification: orderClassification,
        note: note,
        arrivalDate: arrivalDate,
        arrivalTimeZone: arrivalTimeZone,
        expectedShipDate: expectedShipDate,
        shortestArrivalDate: shortestArrivalDate,
        orderClItemsAttributes: orderClItemsAttributes,
        custCodeSuffix: custCodeSuffix
      },
      applicant: {
        nameKana: applicant.nameKana,
        birthday: applicant.birthday,
        applicantNameOpen: applicantNameOpen,
      }
    }

    try {
      await confirmFacilityLens(params);
      setAlertMessageOpen(false);

      localStorage.setItem("isLeftLensOrdered", JSON.stringify(isLeftLensOrdered));
      localStorage.setItem("isRightLensOrdered", JSON.stringify(isRightLensOrdered));
      localStorage.setItem("orderParams", JSON.stringify(params));
      localStorage.setItem("startMonth", JSON.stringify(startMonth));
      localStorage.setItem("facilityId", JSON.stringify(facility?.id));
      localStorage.setItem("facilityCode", JSON.stringify(facility?.facilityCode));
      localStorage.setItem("facilityFlatRateCode", JSON.stringify(facility?.facilityFlatRateCode));
      localStorage.setItem("facilityName", JSON.stringify(facility?.name));
      localStorage.setItem("productName", JSON.stringify(productName));
      localStorage.setItem("applicant", JSON.stringify(applicant));
      localStorage.setItem("customerNewOrder", JSON.stringify(customerNewOrder));
      localStorage.setItem("applicantNameOpen", JSON.stringify(applicantNameOpen));

      navigate("/facility/orders/facility_lens/confirm");
      window.scrollTo(0, 0);
    } catch (err: any) {
      setAlertMessageOpen(true);
      if (err.response && err.response.data && err.response.data.errors) {
        setAlertMessageOpen(true);
        setAlertMessages(err.response.data.errors.fullMessages);
        window.scrollTo(0, 0);
      } else {
        setAlertMessageOpen(false);
        console.log('An unexpected error occurred:', err);
      }
    }
  }

  const classificationValidKeys = ["brandNew", "planChange", "prescriptionReplacement", "damageReplacement", "stainReplacement", "renewal", "lostNonWarranty"];
  const classificationOptions = Object.entries(orderClassificationLabels)
  .filter(([key, _]) => classificationValidKeys.includes(key))
  .map(([key, label]) => ({
    value: toUnderscoreCase(key),
    label: label
  }));

  useEffect(() => {
    if (custCodeSuffix.length === 6 && facility) {
      fetchStartMonth();
      checkExistOrder();
    } else {
      setStartMonth("");
      setCustomerNewOrder(false);
    }
  }, [custCodeSuffix, facility]);

  const fetchStartMonth = async () => {
    try {
      const response = await findStartMonth({ custCode: `${facility!.facilityFlatRateCode}${custCodeSuffix}` });
      setStartMonth(response.data.startMonth);
    } catch (error) {
      console.error("Error fetching startMonth:", error);
      setStartMonth("");
    }
  };

  const checkExistOrder = async () => {
    try {
      const response = await checkExistCustomerOrder(`${facility!.facilityFlatRateCode}${custCodeSuffix}`);
      setCustomerNewOrder(!response.data.exists);
    } catch (error) {
      console.error("Error check exist order:", error);
      setCustomerNewOrder(false);
    }
  };

  return (
    <React.Fragment>
      <Layout>
        <main id="page-lens_order" className="wrapper">
          <section className="sec-contact">
            <div className="inner">
              <div className="sec-contact__item">
                <h1 className="c-ttl__01">施設型定額制レンズ注文</h1>
                <AlertMessages open={alertMessageOpen} messages={alertMessages} />
                <div className="contact-box">
                  <div className="contact-box__left">
                    <form onSubmit={handleSubmit}>
                      <div className="contact-box__form mbsp-50 js-item" id="id-01">
                        <h2 className="c-ttl__02">お客様情報</h2>
                        <InfoDisplay label="コードNo" value={facility?.facilityCode} />
                        <InfoDisplay label="得意先名" value={facility?.name} />
                        <div className="contact-box__item contact-box__item--start">
                          <label className="contact-box__ttl">
                            <span>お客様ID</span>
                            <small>必須</small>
                          </label>
                          <div className="contact-box__ct">
                            <div className="contact-box__flex">
                              <p className="contact-box__desc">{facility?.facilityFlatRateCode}</p>
                              <input type="text" className="input input-id" placeholder="123456" value={custCodeSuffix} onChange={event => setCustCodeSuffix(event.target.value)} />
                            </div>
                            <p className="contact-box__note">※6桁の数字でご入力</p>
                          </div>
                        </div>
                        <InputFieldBox label={"お客様名 (ｶﾅ)"} placeholder={"ﾚﾝｽﾞ ﾊﾅｺ"} value={custNameKana} onChange={event => setCustNameKana(event.target.value)} required={true} />
                        { customerNewOrder &&
                          orderClassification === "brand_new" &&
                          <div className="contact-box__item">
                            <label className="contact-box__ttl">
                              <span>申込者名(カナ)</span>
                              <small>必須</small>
                            </label>
                            <div className="contact-box__ct">
                              {applicantNameOpen &&
                                <input type="text" className="input" placeholder="タナカ　イチロウ" value={applicant.nameKana} onChange={event => setApplicant({...applicant, nameKana: event.target.value })} />
                              }
                              <ul className="list-radio" style={{marginTop: "1em"}}>
                                <li className="list-radio__item">
                                  <label className="checkbox-item correct-box__form-check">
                                    <input type="checkbox" id="checkHideDateField" checked={!applicantNameOpen} onChange={event => {event.target.checked ? setApplicantNameOpen(false) : setApplicantNameOpen(true)}} />
                                    <span className="checkmark"></span> お客様と同一
                                  </label>
                                </li>
                              </ul>
                            </div>
                          </div>
                        }
                        { customerNewOrder &&
                          orderClassification === "brand_new" &&
                          <DateFieldBox
                            label={"申込者生年月日"}
                            pattern={"d{4}-d{2}-d{2}"}
                            value={applicant.birthday}
                            onChange={event => setApplicant({...applicant, birthday: event.target.value })}
                            required={true}
                          />
                        }
                      </div>
                      <div className="contact-box__form mbsp-29 js-item" id="id-02">
                        <h2 className="c-ttl__02">注文内容</h2>
                        <SelectInputBox label="注文区分"
                          value={orderClassification}
                          options={classificationOptions}
                          onChangeFunctions={[(newValue) => setOrderClassification(newValue || '')]}
                          contactBoxCt="contact-box__fcl"
                        />
                        { orderClassification !== "brand_new" &&
                          orderClassification !== "lost_non_warranty" &&
                          <InfoDisplay label="サービス利用開始月" value={formatMonth(startMonth)} />
                        }
                        <SelectInputBox
                          label="対象レンズ"
                          required={true}
                          options={getProductOptions(products)}
                          value={productId ? productId.toString() : ""}
                          placeholder="選択してください"
                          onChangeFunctions={[(newValue) => handleProductIdChange(newValue || "", setProductId)]}
                          size="select-custom--full"
                        />
                        <div className="contact-box__item contact-box__item--start">
                          <label className="contact-box__ttl">
                            <span>レンズデータ</span>
                            <small>必須</small>
                          </label>
                          <div className="contact-box__ct">
                            { (restrict === 'no_restrict' || restrict === 'only_right') &&
                              <LensDataForm
                                eyeType={'right'}
                                isLensOrdered={isRightLensOrdered}
                                setIsLensOrdered={setIsRightLensOrdered}
                                lens={rightLens}
                                setLens={setRightLens}
                                stockOut={rightStockOut}
                                qtyMax={qtyMax}
                                productId={productId}
                                isAstigma={isAstigma}
                                products={products}
                              />
                            }
                            { (restrict === 'no_restrict' || restrict === 'only_left') &&
                              <LensDataForm
                                eyeType={'left'}
                                isLensOrdered={isLeftLensOrdered}
                                setIsLensOrdered={setIsLeftLensOrdered}
                                lens={leftLens}
                                setLens={setLeftLens}
                                stockOut={leftStockOut}
                                qtyMax={qtyMax}
                                productId={productId}
                                isAstigma={isAstigma}
                                products={products}
                              />
                            }
                          </div>
                        </div>
                        <InputFieldBox label="備考" value={note} onChange={e => setNote(e.target.value)} />
                        <SelectInputBox
                          label="着日指定"
                          required={true}
                          options={arrivalDates.map(date => ({ value: date, label: date }))}
                          value={arrivalDate}
                          placeholder="最短お届け日"
                          onChangeFunctions={[(newValue) => setArrivalDate(newValue || '')]}
                        />
                        <SelectInputBox
                          label="ご希望時間帯"
                          required={true}
                          options={Object.entries(arrivalTimeZoneLabels || {}).map(([key, label]) => ({
                            value: toUnderscoreCase(key),
                            label
                          }))}
                          value={arrivalTimeZone}
                          onChangeFunctions={[(newValue) => setArrivalTimeZone(newValue || "")]}
                        />
                      </div>
                      <button type="submit" className="c-btn__01" disabled={!!rightStockOut || !!leftStockOut}>注文確認</button>
                    </form>
                  </div>
                  <SidebarNavigation items={[{ id: "id-01", label: "お客様情報" }, { id: "id-02", label: "注文内容" }]} />
                </div>
              </div>
            </div>
          </section>
        </main>
      </Layout>
    </React.Fragment>
  )
}

export default FacilityLens;